import type { FeatureFlagKey } from "../types/featureFlags";
import type { UserProfile } from "../types/auth";
import { PLAN_FEATURES, type PlanFeatureKey } from "../types/planTiers";

/**
 * Page permissions an organization admin can withhold from a staff member.
 * Mirrors `Constants/Permissions.cs` in the API — change both together.
 */
export const PERMISSIONS = {
  // The college coach's Home page (`api/home`), behind the `home.dashboard` flag. Withheld, the
  // coach lands on Players instead.
  home: "home",
  calls: "calls",
  video: "video",
  messaging: "messaging",
  notes: "notes",
  rosters: "rosters",
  schedule: "schedule",
  // Spelled with an underscore, byte-for-byte as `Permissions.TrustedContacts` in the API. The
  // comparison there is case-insensitive but not separator-insensitive, and a mismatch fails open
  // here and closed at the API — a page that renders and then 403s on every request.
  trustedContacts: "trusted_contacts",
  duplicates: "duplicates",
  // Tournament Finder. Spelled byte-for-byte as `Permissions.Tournaments` in the API.
  tournaments: "tournaments",
  // The Player Database. Spelled byte-for-byte as `Permissions.PlayerSearch` in the API. Not
  // `players`: that names the coach's own Players page, which is always available.
  playerSearch: "player_search",
  // Travel Ball discovery (college coaches reading opted-in programs' rosters). Spelled
  // byte-for-byte as `Permissions.TravelBallSearch` in the API.
  travelBallSearch: "travel_ball_search",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * What each page is called when we have to name it — currently the access-denied screen.
 *
 * Mirrors the labels in `Permissions.All` on the API, which is what the admin's Page Access
 * toggles already show, so a coach reads the same name in both places. Keeping it here rather
 * than passing a string per route stops the name from being spelled a fifth way.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  home: "Home",
  calls: "Calls",
  video: "Video",
  messaging: "Messaging",
  notes: "Latest Notes",
  rosters: "Roster Planning",
  schedule: "Schedule",
  trusted_contacts: "Trusted Contacts",
  duplicates: "Merge Manager",
  tournaments: "Event Finder",
  player_search: "Player Search",
  travel_ball_search: "Travel Ball",
};

/**
 * Pages that belong to a feature behind a flag. While the flag is off for the viewer the page is not
 * offered in the Page Access editor, so an org admin is never shown control over a dark feature.
 */
export const PERMISSION_FEATURE_FLAGS: Partial<Record<Permission, FeatureFlagKey>> = {
  tournaments: "directory.tournaments",
  player_search: "directory.players",
  travel_ball_search: "travelball.foundation",
  messaging: "messaging.sent",
  home: "home.dashboard",
};

/**
 * Pages that belong to a plan feature (baseball plan tiers). While the program's plan leaves the
 * feature out, the page is not offered in the Page Access editor either. Fails open like
 * `hasPlanFeature`: no `planFeatures` on the profile means every feature.
 */
export const PERMISSION_PLAN_FEATURES: Partial<Record<Permission, PlanFeatureKey>> = {
  messaging: PLAN_FEATURES.textMessaging,
};

/**
 * Whether this profile may reach `permission`.
 *
 * **Absent means allowed**, in three separate cases that all have to fail open:
 *
 * - the profile is still loading, so nothing is known yet
 * - the API predates this feature and sends no `permissions` field
 * - the field was lost somewhere in between
 *
 * Denying on absence would be indistinguishable from every coach in every organization being
 * restricted at once — the same reasoning as `messagingEnabled` in `types/auth.ts`.
 *
 * This is presentation only. The API enforces the same restriction with `RequirePermissionAttribute`,
 * which is what actually stops a bookmark or a pasted URL.
 */
export const hasPermission = (
  profile: UserProfile | undefined,
  permission: Permission
): boolean => {
  const permissions = profile?.permissions;
  if (!permissions) return true;
  return permissions.includes(permission);
};
